import React from "react";
import { FiEdit2, FiTrash2, FiPlus } from "react-icons/fi";
import type { KeyPersonnelProject } from "../types/KeyPersonnel.type";

interface ProjectExperienceTableProps {
	projects: KeyPersonnelProject[];
	onAdd: () => void;
	onEdit: (index: number) => void;
	onRemove: (index: number) => void;
}

export const ProjectExperienceTable: React.FC<ProjectExperienceTableProps> = ({
	projects,
	onAdd,
	onEdit,
	onRemove,
}) => {
	return (
		<div className="flex flex-col" data-testid="project-experience-table">
			<div className="overflow-x-auto rounded-lg border border-gray-200">
				<table className="w-full text-left text-sm">
					<thead className="border-b border-gray-200 bg-gray-50">
						<tr>
							<th className="px-4 py-3 font-medium text-gray-600">Project Name</th>
							<th className="px-4 py-3 font-medium text-gray-600">Role on the Project</th>
							<th className="px-4 py-3 font-medium text-gray-600">Top 5 Sectors</th>
							<th className="px-4 py-3 font-medium text-gray-600">Project Construction Value (AUD)</th>
							<th className="px-4 py-3 font-medium text-gray-600"></th>
						</tr>
					</thead>
					<tbody>
						{projects.length === 0 ? (
							<tr>
								<td
									colSpan={5}
									className="px-4 py-8 text-center text-gray-400"
									data-testid="empty-projects"
								>
									No projects added
								</td>
							</tr>
						) : (
							projects.map((project, index) => (
								<tr
									key={index}
									className="border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-blue-50"
									data-testid={`project-row-${index}`}
									onClick={() => onEdit(index)}
								>
									<td className="px-4 py-3 text-gray-800">{project.name}</td>
									<td className="px-4 py-3 text-gray-600">{project.role}</td>
									<td className="px-4 py-3 text-gray-600">
										{project.sectors?.length
											? project.sectors.map((s) => s.name).join(", ")
											: project.sector?.name ?? "-"}
									</td>
									<td className="px-4 py-3 text-gray-600">
										{project.value_humanized || "-"}
									</td>
									<td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
										<div className="flex items-center gap-2">
											<button
												type="button"
												onClick={() => onEdit(index)}
												className="rounded p-1.5 text-gray-400 hover:bg-blue-50 hover:text-blue-500"
												aria-label={`Edit ${project.name}`}
												data-testid={`edit-project-${index}`}
											>
												<FiEdit2 size={16} />
											</button>
											<button
												type="button"
												onClick={() => onRemove(index)}
												className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500"
												aria-label={`Remove ${project.name}`}
												data-testid={`remove-project-${index}`}
											>
												<FiTrash2 size={16} />
											</button>
										</div>
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>
			<button
				type="button"
				onClick={onAdd}
				className="mt-3 flex items-center gap-2 self-start rounded border border-dashed border-gray-300 px-4 py-2 text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600"
				data-testid="add-project-btn"
			>
				<FiPlus size={16} />
				Add Project
			</button>
		</div>
	);
};
